import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  UsersIcon,
  SparklesIcon,
  PlusIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { getDashboard } from '../../api/reportApi';
import { useAuth } from '../../context/AuthContext';
import Badge from '../../components/ui/Badge';
import Card from '../../components/ui/Card';

const STATUS_VARIANT = {
  draft:     'default',
  published: 'success',
  closed:    'warning',
  archived:  'error',
};

function StatCard({ icon: Icon, value, label, iconBg, iconColor }) {
  return (
    <div className="rounded-card bg-white shadow-card ring-1 ring-neutral-200/60 p-6 flex items-center gap-4">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconBg} ${iconColor}`}>
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <div>
        <p className="text-2xl font-bold text-neutral-900 font-heading leading-none">
          {value ?? '—'}
        </p>
        <p className="text-sm text-neutral-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

function SkeletonStatCard() {
  return (
    <div className="rounded-card bg-white shadow-card ring-1 ring-neutral-200/60 p-6 flex items-center gap-4 animate-pulse">
      <div className="h-12 w-12 rounded-xl bg-neutral-100 shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-6 w-16 rounded bg-neutral-100" />
        <div className="h-4 w-28 rounded bg-neutral-100" />
      </div>
    </div>
  );
}

function SkeletonTableRows() {
  return (
    <tbody className="divide-y divide-neutral-50 bg-white animate-pulse">
      {[1, 2, 3].map((i) => (
        <tr key={i}>
          <td className="px-6 py-3.5"><div className="h-4 w-48 rounded bg-neutral-100" /></td>
          <td className="px-4 py-3.5"><div className="h-5 w-16 rounded-full bg-neutral-100" /></td>
          <td className="px-4 py-3.5"><div className="h-4 w-8 rounded bg-neutral-100 ml-auto" /></td>
          <td className="px-4 py-3.5"><div className="h-4 w-24 rounded bg-neutral-100" /></td>
          <td className="px-6 py-3.5"><div className="h-4 w-12 rounded bg-neutral-100 ml-auto" /></td>
        </tr>
      ))}
    </tbody>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboard()
      .then((res) => setStats(res.data.data))
      .catch(() => toast.error('Failed to load dashboard stats'))
      .finally(() => setLoading(false));
  }, []);

  const firstName = user?.name?.split(' ')[0] ?? user?.email ?? 'there';

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 font-heading">Dashboard</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            Welcome back,{' '}
            <span className="font-medium text-neutral-700">{firstName}</span>! Here's what's happening.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/questions" className="btn-secondary text-sm">
            <BookOpenIcon className="h-4 w-4" />
            Question Library
          </Link>
          <Link to="/assessments/new" className="btn-primary text-sm">
            <PlusIcon className="h-4 w-4" />
            New Assessment
          </Link>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading ? (
          <>
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
            <SkeletonStatCard />
          </>
        ) : (
          <>
            <StatCard
              icon={ClipboardDocumentListIcon}
              value={stats?.total_assessments}
              label="Total Assessments"
              iconBg="bg-primary-50"
              iconColor="text-primary-600"
            />
            <StatCard
              icon={CheckCircleIcon}
              value={stats?.active_assessments}
              label="Active Assessments"
              iconBg="bg-success-50"
              iconColor="text-success-600"
            />
            <StatCard
              icon={UsersIcon}
              value={stats?.total_participants}
              label="Total Participants"
              iconBg="bg-secondary-50"
              iconColor="text-secondary-600"
            />
            <StatCard
              icon={SparklesIcon}
              value={stats?.analyses_run}
              label="Analyses Run"
              iconBg="bg-warning-50"
              iconColor="text-warning-600"
            />
          </>
        )}
      </div>

      {/* Recent Assessments table */}
      <Card
        title="Recent Assessments"
        actions={
          <Link
            to="/assessments"
            className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            View all →
          </Link>
        }
      >
        {loading ? (
          <div className="-mx-6 -mb-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-100">
              <thead>
                <tr className="bg-neutral-50">
                  {['Title', 'Status', 'Participants', 'Created', 'Action'].map((h) => (
                    <th
                      key={h}
                      className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <SkeletonTableRows />
            </table>
          </div>
        ) : !stats?.recent_assessments?.length ? (
          <div className="py-12 text-center text-sm text-neutral-500">
            No assessments yet.{' '}
            <Link to="/assessments/new" className="font-medium text-primary-600 hover:underline">
              Create your first one
            </Link>
            .
          </div>
        ) : (
          <div className="-mx-6 -mb-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-100">
              <thead>
                <tr className="bg-neutral-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Participants
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-50 bg-white">
                {stats.recent_assessments.map((a) => (
                  <tr key={a.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-6 py-3.5 text-sm font-medium text-neutral-900 max-w-xs truncate">
                      {a.title}
                    </td>
                    <td className="px-4 py-3.5">
                      <Badge variant={STATUS_VARIANT[a.status] ?? 'default'}>
                        {a.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm text-neutral-600">
                      {a.participant_count ?? 0}
                    </td>
                    <td className="px-4 py-3.5 text-sm text-neutral-500">
                      {a.created_at
                        ? new Date(a.created_at).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <Link
                        to={`/assessments/${a.id}`}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick actions */}
      <div>
        <h2 className="text-base font-semibold text-neutral-900 font-heading mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => navigate('/assessments/new')}
            className="flex items-center gap-3 rounded-card border border-neutral-200 bg-white px-5 py-4 text-left shadow-sm hover:border-primary-300 hover:shadow-md transition-all group"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
              <PlusIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-neutral-900 text-sm">New Assessment</p>
              <p className="text-xs text-neutral-500 mt-0.5">Create a new assessment from scratch</p>
            </div>
          </button>
          <button
            type="button"
            onClick={() => navigate('/questions')}
            className="flex items-center gap-3 rounded-card border border-neutral-200 bg-white px-5 py-4 text-left shadow-sm hover:border-primary-300 hover:shadow-md transition-all group"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-50 text-secondary-600 group-hover:bg-secondary-100 transition-colors">
              <BookOpenIcon className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium text-neutral-900 text-sm">Question Library</p>
              <p className="text-xs text-neutral-500 mt-0.5">Browse and manage your question bank</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
